import { useLocation } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useSnapshot } from 'valtio';
import FeatureFlags, { OrgFeatureFlag } from '~/authorizations/FeatureFlags';
import type { User } from '~/common/types';
import { Dropdown, type Item } from '~/components2/Dropdown';
import Icon from '~/components2/Icon';
import { useConflictsSummaryPath } from '~/hooks/useConflictsSummaryPath';
import store from '~/store';

const pageMap = {
  homeMap: {
    type: 'link',
    label: 'Projects',
    url: '/home',
    'data-testid': 'top-nav-projects',
  },
  orgSummary: {
    type: 'link',
    label: 'Organization Summary',
    url: '/orgSummary',
    'data-testid': 'top-nav-org-summary',
  },
  conflictsSummary: {
    type: 'link',
    label: 'Conflicts Summary',
    url: '/conflictsSummary',
    'data-testid': 'top-nav-conflicts-summary',
  },
  conflictsSummaryV2: {
    type: 'link',
    label: 'Conflicts Summary',
    url: '/conflictsSummaryV2',
    'data-testid': 'top-nav-conflicts-summary-v2',
  },
  billingSummary: {
    type: 'link',
    label: 'Billing Summary',
    url: '/billingSummary',
    'data-testid': 'top-nav-billing-summary',
  },
  dataRequestFulfillment: {
    type: 'link',
    label: 'Data Request Fulfillment',
    url: '/dataRequestFulfillment',
    'data-testid': 'top-nav-data-request-fulfillment',
  },
} as const;

export interface PageSwitcherInputs {
  isAdmin: boolean;
  isOrgLoaded: boolean;
  isDemoOrg: boolean;
  hasConflictsSummary: boolean;
  hasDrf: boolean;
  conflictsSummaryPath: string;
}

export function getPageSwitcherItems({
  isAdmin,
  isOrgLoaded,
  isDemoOrg,
  hasConflictsSummary,
  hasDrf,
  conflictsSummaryPath,
}: PageSwitcherInputs): Item[] {
  const items: Item[] = [];

  // Admin-only pages stay hidden until the current org has loaded, and always in a demo org.
  if (isAdmin && isOrgLoaded && !isDemoOrg) {
    items.push(pageMap.orgSummary, pageMap.billingSummary);
  }

  if (hasConflictsSummary) {
    items.push(conflictsSummaryPath === '/conflictsSummaryV2' ? pageMap.conflictsSummaryV2 : pageMap.conflictsSummary);
  }

  if (hasDrf) {
    items.push(pageMap.dataRequestFulfillment);
  }

  if (items.length) {
    items.unshift(pageMap.homeMap, { type: 'separator' });
  }

  return items;
}

function isAdmin(me: User | null): boolean {
  return me?.isAdmin === true;
}

function hasFlag(flag: OrgFeatureFlag): boolean {
  return FeatureFlags.hasOrgFeatureFlag(flag);
}

export default function PageSwitcher() {
  const { pathname } = useLocation();
  const { me, currentOrganizationId, currentOrganizationDetail, inUnverifiedMode } = useSnapshot(store) as typeof store;
  const conflictsSummaryPath = useConflictsSummaryPath();
  const isOrgLoaded = currentOrganizationDetail.isLoading === false;

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const dropdownItems = useMemo<Item[]>(
    () =>
      getPageSwitcherItems({
        isAdmin: isAdmin(me),
        isOrgLoaded,
        isDemoOrg: hasFlag(OrgFeatureFlag.DemoOrg),
        hasConflictsSummary: hasFlag(OrgFeatureFlag.ConflictsSummary),
        hasDrf: hasFlag(OrgFeatureFlag.Drf),
        conflictsSummaryPath,
      }),
    [currentOrganizationId, me?.id, isOrgLoaded, conflictsSummaryPath],
  );

  const isVisible = !!dropdownItems.length;
  let triggerText = 'UNSET';

  if (pathname.includes('/home')) {
    triggerText = 'Projects';
  } else if (pathname.includes('/projects')) {
    triggerText = 'Projects';
  } else if (pathname.includes('/orgSummary')) {
    triggerText = 'Organization Summary';
  } else if (pathname.includes('/conflictsSummary')) {
    triggerText = 'Conflicts Summary';
  } else if (pathname.includes('/billingSummary')) {
    triggerText = 'Billing Summary';
  } else if (pathname.includes('/dataRequestFulfillment')) {
    triggerText = 'Data Request Fulfillment';
  } else if (pathname.includes('/orgSettings') || pathname.includes('/settings/layerLibrary')) {
    triggerText = 'Settings';
  } else if (pathname.includes('/admin')) {
    triggerText = 'Admin';
  }

  if (isVisible && !inUnverifiedMode) {
    return (
      <Dropdown
        align="start"
        data-testid="top-nav-page-switcher-trigger"
        triggerElement={
          <button
            type="button"
            className="flex cursor-pointer items-center space-x-2 pl-2 text-sm font-noto font-medium text-grey-90 rounded-[6px] focus-ring"
          >
            <div>{triggerText}</div>
            <Icon icon="chevronDown" />
          </button>
        }
        items={dropdownItems}
      />
    );
  } else {
    return null;
  }
}
